import StageConfigDialog from "./StageConfigDialog";
import * as utils from "./utils";
import {
  addVisibilityRoleExceptionFunc,
  addVisibilityUserAccessFunc,
  clearVisibilityExceptionRoleFunc,
  clearVisibilityUserAccessFunc,
  deleteComponentVisibilityFunc,
  formatLoadedUserConfigValue,
  removeVisibilityRoleExceptionFunc,
  removeVisibilityUserAccessFunc,
  setComponentVisibilityFunc,
} from "./utils";
import { Alert, Grid, Stack, TextField } from "@mui/material";
import Component from "components/Component";
import ComponentVisibilityEditor from "components/admin/editors/ComponentVisibilityEditor";
import BigLoader from "components/common/BigLoader";
import Button from "components/common/Button";
import InlineSearch from "components/common/InlineSearch";
import * as config from "config";
import { useSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import * as adminService from "services/adminService";
import * as userService from "services/userService";
import * as adminActions from "store/actions/temp/admin/adminActions";
import * as adminSelectors from "store/selectors/temp/admin/adminSelectors";

const UserConfigOverlay = ({
  saveUserLogMessage,
  hasUserLogMessage,
  userLogMessage,
}) => {
  const [formattedUserConfigValue, setFormattedUserConfigValue] = useState(
    null
  );
  const [searchText, setSearchText] = useState("");
  const [stageDialogOpen, setStageDialogOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const { companyId } = useParams();

  const {
    currentData: userConfig,
    isLoading: isUserConfigLoading,
    isError: isUserConfigError,
    refetch: refetchUserConfig,
  } = userService.useRetrieveConfigQuery(
    { companyId },
    { skip: !companyId, refetchOnMountOrArgChange: true }
  );

  const formattedUserConfig = useMemo(
    () => formatLoadedUserConfigValue(userConfig),
    [userConfig]
  );

  const [
    updateUserConfig,
    { isLoading: isUpdateRunning, isError: isUpdateError },
  ] = adminService.useUpdateUserConfigMutation();

  const [
    triggerListUsers,
    { data: users },
  ] = adminService.useLazyListUsersQuery();

  useEffect(() => {
    if (companyId) triggerListUsers({ companyId });
  }, [companyId, triggerListUsers]);

  useEffect(() => {
    setFormattedUserConfigValue(formattedUserConfig);
  }, [formattedUserConfig]);

  const setComponentVisibility = (component, visibility) => {
    setFormattedUserConfigValue(
      setComponentVisibilityFunc(component, visibility)
    );
    setFormattedUserConfigValue(clearVisibilityExceptionRoleFunc(component));
  };

  const deleteComponentVisibility = (component) => {
    setFormattedUserConfigValue(deleteComponentVisibilityFunc(component));
    setFormattedUserConfigValue(clearVisibilityExceptionRoleFunc(component));
    setFormattedUserConfigValue(clearVisibilityUserAccessFunc(component));
  };

  const addVisibilityExceptionRole = (component, role) => {
    setFormattedUserConfigValue(
      addVisibilityRoleExceptionFunc(component, role)
    );
  };

  const removeVisibilityExceptionRole = (component, role) => {
    setFormattedUserConfigValue(
      removeVisibilityRoleExceptionFunc(component, role)
    );
  };

  const clearVisibilityExceptionRole = (component) => {
    setFormattedUserConfigValue(clearVisibilityExceptionRoleFunc(component));
  };

  const addVisibilityUserAccess = (component, userId) => {
    setFormattedUserConfigValue(addVisibilityUserAccessFunc(component, userId));
  };

  const removeVisibilityUserAccess = (component, userId) => {
    setFormattedUserConfigValue(
      removeVisibilityUserAccessFunc(component, userId)
    );
  };

  const setComponentOnStateVisiblity = (
    component,
    type,
    index,
    key,
    values
  ) => {
    setFormattedUserConfigValue((oldUserConfig) => ({
      ...oldUserConfig,
      componentOnStateVisibility: {
        ...oldUserConfig.componentOnStateVisibility,
        [component]: {
          ...oldUserConfig?.componentOnStateVisibility?.[component],
          [type]: [
            ...(
              oldUserConfig.componentOnStateVisibility?.[component]?.[type] ??
              []
            ).map(([oldKey, oldValues], i) =>
              i === index ? [key, values] : [oldKey, oldValues]
            ),
            ...(index >
            (
              oldUserConfig.componentOnStateVisibility?.[component]?.[type] ??
              []
            ).length -
              1
              ? [[key, values]]
              : []),
          ].filter(
            ([key, values], i, arr) =>
              i === arr.length - 1 ||
              key.length > 0 ||
              (values.length > 0 && values[0].length > 0)
          ),
        },
      },
    }));
  };

  return (
    <Component
      title={"Component Visibility"}
      spacing={config.GRID_SPACING}
      options={
        <InlineSearch
          placeholder={"Quick Filter"}
          onChange={(searchText) => setSearchText(searchText.toLowerCase())}
        />
      }
    >
      {isUserConfigLoading || isUpdateRunning ? (
        <BigLoader />
      ) : isUserConfigError ? (
        <Alert severity={"error"}>{"Failed to load."}</Alert>
      ) : !!formattedUserConfig ? (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <ComponentVisibilityEditor
              componentVisibilities={
                formattedUserConfigValue?.componentVisibility ?? {}
              }
              stateVisibilities={
                formattedUserConfigValue?.componentOnStateVisibility
              }
              visibilityExceptions={
                formattedUserConfigValue?.componentVisibilityRoleExceptions ??
                {}
              }
              visibilityUserAccess={
                formattedUserConfigValue?.componentVisibilityUserAccess ?? {}
              }
              users={users ?? []}
              setComponentVisibility={setComponentVisibility}
              setComponentOnStateVisiblity={setComponentOnStateVisiblity}
              deleteComponentVisibility={deleteComponentVisibility}
              addVisibilityRoleException={addVisibilityExceptionRole}
              removeVisibilityRoleException={removeVisibilityExceptionRole}
              clearVisibilityRoleExceptions={clearVisibilityExceptionRole}
              addVisibilityUserAccess={addVisibilityUserAccess}
              removeVisibilityUserAccess={removeVisibilityUserAccess}
              searchText={searchText}
            />
          </Grid>
          <Grid
            container
            item
            xs={12}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Grid item xs>
              <TextField
                variant={"filled"}
                label={"Reason for change"}
                fullWidth
                onChange={(e) => saveUserLogMessage(e.target.value)}
                value={userLogMessage}
              />
            </Grid>
            <Grid item>
              <Stack direction={"row"} gap={1}>
                <Button
                  color={"secondary"}
                  onClick={() => setStageDialogOpen(true)}
                  disabled={!formattedUserConfigValue || isUpdateRunning}
                >
                  {"Stage"}
                </Button>
                <Button
                  onClick={async () => {
                    await updateUserConfig({
                      companyId,
                      userLogMessage,
                      value: utils.formatSavedValue(formattedUserConfigValue),
                    });
                    refetchUserConfig();
                  }}
                  disabled={
                    !formattedUserConfigValue ||
                    isUpdateRunning ||
                    !hasUserLogMessage
                  }
                  isLoading={isUpdateRunning}
                  hasError={isUpdateError}
                  errorTooltip={"Failed to save"}
                >
                  {"Save"}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Grid>
      ) : (
        <></>
      )}
      <StageConfigDialog
        open={stageDialogOpen}
        onClose={(staged) => {
          setStageDialogOpen(false);
          if (staged) {
            enqueueSnackbar("Component visibility change staged for release", {
              variant: "success",
            });
          }
        }}
        companyId={companyId}
        staticDataKey={"user_config"}
        changeType={"user_config"}
        getData={() => utils.formatSavedValue(formattedUserConfigValue)}
        initialReason={userLogMessage}
      />
    </Component>
  );
};

const mapStateToProps = (state) => {
  return {
    userLogMessage: adminSelectors.userLogMessage(state),
    hasUserLogMessage: adminSelectors.hasUserLogMessage(state),
  };
};

const mapDispatchToProps = {
  saveUserLogMessage: adminActions.userLogMessage.update,
};

export default connect(mapStateToProps, mapDispatchToProps)(UserConfigOverlay);
